function abortError() {
    const error = new Error('Polling aborted.');
    error.name = 'AbortError';
    return error;
}

function waitForDelay(delayMs, signal) {
    return new Promise((resolve, reject) => {
        if (signal?.aborted) {
            reject(abortError());
            return;
        }

        const timer = setTimeout(() => {
            signal?.removeEventListener('abort', handleAbort);
            resolve();
        }, delayMs);
        const handleAbort = () => {
            clearTimeout(timer);
            reject(abortError());
        };

        signal?.addEventListener('abort', handleAbort, { once: true });
    });
}

/**
 * Repeats an asynchronous request while the caller considers its value
 * retryable. Protocol interpretation and retry timing remain caller-owned.
 */
export async function poll({
    request,
    shouldRetry,
    retryDelayMs = 0,
    signal,
    onRetry = () => {},
    maxAttempts = 1,
    wait = waitForDelay
}) {
    const attemptLimit = Math.max(1, Math.floor(maxAttempts));

    for (let attempt = 1; attempt <= attemptLimit; attempt++) {
        if (signal?.aborted) throw abortError();

        const value = await request({ attempt, signal });
        if (signal?.aborted) throw abortError();

        if (!shouldRetry(value, attempt)) {
            return { state: 'complete', value, attempts: attempt };
        }

        onRetry(value, attempt);
        if (attempt === attemptLimit) {
            return { state: 'exhausted', value, attempts: attempt };
        }

        const delayMs =
            typeof retryDelayMs === 'function'
                ? retryDelayMs(value, attempt)
                : retryDelayMs;
        await wait(Math.max(0, Number(delayMs) || 0), signal);
    }

    throw new Error('Polling ended unexpectedly.');
}
